import type { DailyStats, StreakState, TrendPoints, WindowUsage } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BeliefPlate, WhenPlate } from '../components/Plates.js';
import { CanSayTable, InsideDay, MarksLedger, RecordGrid, ThreadLanes, WeeksPlate, canSayRows, dayCells, daysBetween, shortDay } from '../components/RecordPlates.js';

/**
 * Insights: the record, drawn as points. Release 6b of 2.0 (docs/PRD-2.0.md
 * §6; the drawing is docs/pitch-2026-10-12/mocks/insights.html).
 *
 * The screen this replaces led with a count and a paragraph about why the
 * marks can say nothing. This one draws every rated day as a point, says its
 * n on every chart, and ends with the list of what the record can answer
 * today and the one change that would let it answer more. Nothing is ordered
 * by effect and no mean faces the reader: the noise ceiling (DESIGN §11, the
 * v2 plan) is why — every day-mark contrast in his record is inside the
 * retest error of a single mood rating, and a ranked bar would print advice.
 *
 * The route keeps its old name, /trends. The default window is everything,
 * because the record is the product; 90 and 30 days are the other two.
 */

interface TrendsResponse {
  from: string;
  to: string;
  daily: DailyStats[];
  streak: StreakState;
  usage?: WindowUsage;
  timezone?: string;
  checkinHours?: number[];
  beliefPairs?: { before: number; after: number }[];
  lifeEvents?: { label: string; startedOn: string; endedOn: string | null }[];
  points?: TrendPoints;
}

type Window = 'all' | 90 | 30;

export function Trends(): JSX.Element {
  const [data, setData] = useState<TrendsResponse | null>(null);
  const [window_, setWindow] = useState<Window>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    const days = window_ === 'all' ? 365 : window_;
    fetch(`/api/insights/trends?days=${days}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("Couldn't load your record. Check your connection, then reload the page."))))
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, [window_]);

  if (error) return <div className="banner banner-error">{error}</div>;
  if (!data) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  // "Everything" is from the first day anything was logged, not 365 days of
  // blank before it — a new account is not shown a year of misses.
  const first = data.streak.firstActiveDay ?? data.from;
  const from = window_ === 'all' && first > data.from ? first : data.from;
  const daily = data.daily.filter((d) => d.day >= from);
  const points: TrendPoints = data.points ?? { checkins: [], marks: [], marksSince: null, ratings: { energy: 0, overwhelm: 0, restlessness: 0, anxiety: 0 }, helpNow: { visits: 0, pairs: 0 }, sleepDays: 0 };
  const checkins = points.checkins.filter((c) => c.day >= from);
  const doseGapDays = new Set(daily.filter((d) => d.day !== data.to && d.medsScheduled > 0 && d.medsTakenScheduled < d.medsScheduled).map((d) => d.day));
  const cells = dayCells(daily, checkins, doseGapDays);
  const rated = cells.filter((c) => c.end !== null).length;
  const inside = new Set(checkins.map((c) => c.day)).size === 0 ? 0 : [...checkins.reduce((m, c) => m.set(c.day, (m.get(c.day) ?? 0) + 1), new Map<string, number>()).values()].filter((n) => n >= 2).length;
  const worksheets = daily.reduce((n, d) => n + d.thoughtRecords, 0);
  const entries = daily.reduce((n, d) => n + d.voiceLogs, 0);
  const wroteDays = daily.filter((d) => d.voiceLogs > 0 || d.thoughtRecords > 0).length;
  const span = daysBetween(from, data.to) + 1;

  return (
    <div className="stack-lg stack insights">
      <header className="page-header">
        <Link className="linkish" to="/days" style={{ display: 'inline-block', marginBottom: 'var(--space-2)' }}>
          ‹ Days
        </Link>
        <h1>Insights</h1>
        <p>
          Your record, drawn. {data.streak.totalActiveDays === 0 ? 'Nothing logged yet.' : `${data.streak.totalActiveDays} ${data.streak.totalActiveDays === 1 ? 'day' : 'days'} so far.`}
        </p>
      </header>

      <div className="chips">
        {([['all', 'Everything'], [90, '90 days'], [30, '30 days']] as const).map(([v, label]) => (
          <button key={String(v)} className="chip" aria-pressed={window_ === v} onClick={() => setWindow(v)}>
            {label}
          </button>
        ))}
      </div>

      <section className="stack">
        <h2>The record</h2>
        <p className="faint n">{shortDay(from)} – {shortDay(data.to)} · {rated} of {span} days rated</p>
        <RecordGrid cells={cells} />
        {(data.lifeEvents ?? []).length > 0 && (
          <>
            <p className="faint">What was running, as lanes across the same stretch:</p>
            <ThreadLanes events={data.lifeEvents ?? []} from={from} to={data.to} />
          </>
        )}
      </section>

      <section className="stack">
        <h2>When</h2>
        <p className="faint n">{checkins.length} check-ins · {data.timezone ?? 'your timezone'}</p>
        <WhenPlate hours={(data.checkinHours ?? []).length === checkins.length ? data.checkinHours ?? [] : checkins.map((c) => c.hour)} timezone={data.timezone ?? null} />
      </section>

      <section className="stack">
        <h2>Inside a day</h2>
        <p className="faint n">{inside} {inside === 1 ? 'day' : 'days'} with two or more check-ins</p>
        <InsideDay cells={cells} checkins={checkins} />
      </section>

      <section className="stack">
        <h2>The weeks</h2>
        <p className="faint n">one dot per rated day</p>
        <WeeksPlate cells={cells} />
      </section>

      <section className="stack">
        <h2>The marks</h2>
        <p className="faint n">{points.marksSince ? `since ${shortDay(points.marksSince)}, when you first used them` : 'none used yet'}</p>
        <MarksLedger cells={cells} marks={points.marks} since={points.marksSince} />
      </section>

      <section className="stack">
        <h2>Worksheets</h2>
        <p className="faint n">{(data.beliefPairs ?? []).length} of {worksheets} with both halves</p>
        <BeliefPlate pairs={data.beliefPairs ?? []} />
      </section>

      <section className="stack">
        <h2>Writing</h2>
        <p className="faint n">{entries} {entries === 1 ? 'entry' : 'entries'} · {worksheets} {worksheets === 1 ? 'worksheet' : 'worksheets'}</p>
        <div className="card chart-plate">
          <ol className="write-strip" aria-label={`Days written on, ${shortDay(from)} to ${shortDay(data.to)}`}>
            {daily.map((d) => (
              <li key={d.day} className={d.voiceLogs > 0 || d.thoughtRecords > 0 ? 'wrote' : ''} title={`${shortDay(d.day)} — ${d.voiceLogs === 0 && d.thoughtRecords === 0 ? 'nothing written' : `${d.voiceLogs} ${d.voiceLogs === 1 ? 'entry' : 'entries'}, ${d.thoughtRecords} ${d.thoughtRecords === 1 ? 'worksheet' : 'worksheets'}`}`} />
            ))}
          </ol>
          <p className="faint">
            You wrote on {wroteDays} of these {daily.length} days.
          </p>
        </div>
      </section>

      <section className="stack">
        <h2>What the record can say yet</h2>
        <p className="muted">And what one change would let it say. Nothing here resets, there is no total, and no row is about you — each is about a question, how much of it the record can answer today, and the one change that would let it answer more.</p>
        <CanSayTable rows={canSayRows(cells, { ...points, checkins }, (data.beliefPairs ?? []).length, worksheets, inside)} />
      </section>
    </div>
  );
}
